import type { WebChatConfig } from "@chatbotgang/web-sdk-core/model";
import {
  WebChatConfigSchema,
  WebChatLauncherPositionEnum,
} from "@chatbotgang/web-sdk-core/model";
import { zodResolver } from "@hookform/resolvers/zod";
import { FIREBASE_SHARED_RESOURCE_CDN_BASE_URL } from "@polifonia/env";
import { theme } from "@polifonia/theme";
import { isNotNil } from "es-toolkit";
import { useMemo } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { ChannelQueriesContext } from "@/pages/channels/pages/detail/channelQueriesContext";
import { LocaleKeyEnum } from "@/pages/channels/pages/detail/wccs/WebChatConfig";

const STATIC_ASSETS_BASE_URL = `${FIREBASE_SHARED_RESOURCE_CDN_BASE_URL}/wccs/clients/STATIC_ASSETS`;
const DEFAULT_LAUNCHER_SPACING_PX = 24;

const defaultWebChatConfig = {
  version: "1",
  brandIdentity: {
    logoUrl: `${STATIC_ASSETS_BASE_URL}/logo.png`,
    logoEnabled: true,
    serviceAvatarUrl: `${STATIC_ASSETS_BASE_URL}/avatar.png`,
    serviceAvatarEnabled: true,
    mainColor: {
      headerBgColor: theme.colors.blue060,
      headerTextColor: theme.colors.neutral000,
      startChatButtonBgColor: theme.colors.blue060,
      startChatButtonTextColor: theme.colors.neutral000,
    },
    chatScreen: {
      msgBubbleColor: theme.colors.blue020,
      msgTextColor: theme.colors.neutral090,
    },
  },
  launcher: {
    widgetImageUrl: `${STATIC_ASSETS_BASE_URL}/webChat.png`,
    desktopSetting: {
      buttonPosition: WebChatLauncherPositionEnum.enum.bottomRight,
      bottomSpacingPx: DEFAULT_LAUNCHER_SPACING_PX,
      inlineSpacingPx: DEFAULT_LAUNCHER_SPACING_PX,
    },
    mobileSetting: {
      buttonPosition: WebChatLauncherPositionEnum.enum.bottomRight,
      bottomSpacingPx: DEFAULT_LAUNCHER_SPACING_PX,
      inlineSpacingPx: DEFAULT_LAUNCHER_SPACING_PX,
    },
  },
  welcomeMessages: {
    "en-US": {
      primaryMessageText: "Welcome",
      primaryMessageEnabled: true,
      secondaryMessageText: "Chat with us",
      secondaryMessageEnabled: true,
      descriptionText: "Ask us anything, we'll reply as soon as we can.",
      descriptionEnabled: true,
      startChatButtonText: "Start Chat",
    },
    "zh-TW": {
      primaryMessageText: "歡迎",
      primaryMessageEnabled: true,
      secondaryMessageText: "線上對話",
      secondaryMessageEnabled: true,
      descriptionText: "歡迎隨時詢問，我們會盡快回覆",
      descriptionEnabled: true,
      startChatButtonText: "開始對話",
    },
    ja: {
      primaryMessageText: "ようこそ！",
      primaryMessageEnabled: true,
      secondaryMessageText: "オンラインチャット",
      secondaryMessageEnabled: true,
      descriptionText:
        "ご不明な点がございましたら、ご遠慮なくお問い合わせください。",
      descriptionEnabled: true,
      startChatButtonText: "チャットをはじめる",
    },
    th: {
      primaryMessageText: "ยินดีต้อนรับ",
      primaryMessageEnabled: true,
      secondaryMessageText: "พูดคุยกับเรา",
      secondaryMessageEnabled: true,
      descriptionText: "หากมีคำถามหรือข้อสงสัย สามารถสอบถามเราได้ทุกเมื่อ...",
      descriptionEnabled: true,
      startChatButtonText: "เริ่มการสนทนา",
    },
  },
  webChatTrigger: {
    displayFilter: {
      type: "all",
    },
  },
} satisfies WebChatConfig;

export const useWebChatConfigFormInternal = () => {
  const { t } = useTranslation();
  const { channel } = ChannelQueriesContext.useData();
  const webChatConfig =
    channel.type === "wccs" ? channel.channelInformation.webChatConfig : null;

  const WebChatConfigSchemaWithValidation = useMemo(
    () =>
      WebChatConfigSchema.superRefine((data, ctx) => {
        LocaleKeyEnum.options.forEach((locale) => {
          const messages = data.welcomeMessages[locale];

          if (
            messages?.primaryMessageEnabled &&
            !messages?.primaryMessageText?.trim()
          ) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: t("webChatConfig.validation.welcomeMessageRequired"),
              path: ["welcomeMessages", locale, "primaryMessageText"],
            });
          }

          if (
            messages?.secondaryMessageEnabled &&
            !messages?.secondaryMessageText?.trim()
          ) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: t("webChatConfig.validation.welcomeSubMessageRequired"),
              path: ["welcomeMessages", locale, "secondaryMessageText"],
            });
          }

          if (
            messages?.descriptionEnabled &&
            !messages?.descriptionText?.trim()
          ) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: t("webChatConfig.validation.descriptionRequired"),
              path: ["welcomeMessages", locale, "descriptionText"],
            });
          }

          if (!messages?.startChatButtonText?.trim()) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: t("webChatConfig.validation.buttonTextRequired"),
              path: ["welcomeMessages", locale, "startChatButtonText"],
            });
          }
        });

        const displayFilter = data.webChatTrigger.displayFilter;
        if (
          displayFilter?.type &&
          displayFilter.type !== "all" &&
          "strings" in displayFilter
        ) {
          if (
            !displayFilter.strings ||
            displayFilter.strings.length === 0 ||
            !displayFilter.strings[0]?.trim()
          ) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: t("webChatConfig.validation.triggerUrlRequired"),
              path: ["webChatTrigger", "displayFilter", "strings", 0],
            });
          }
        }
      }),
    [t],
  );

  return useForm({
    mode: "onSubmit",
    resolver: zodResolver(WebChatConfigSchemaWithValidation),
    defaultValues: isNotNil(webChatConfig)
      ? webChatConfig
      : defaultWebChatConfig,
  });
};

const useWebChatConfigForm = Object.assign(useWebChatConfigFormInternal, {
  defaultWebChatConfig,
});

export { useWebChatConfigForm };
